import React, { useEffect, useRef } from 'react'
import type { GridLineItem } from './salesOrderGridUtils'

export interface SalesOrderRowMenuProps {
  line: GridLineItem
  orderId?: string
  canWrite: boolean
  canDemandWrite: boolean
  isOpen: boolean
  anchorRect: DOMRect | null
  onClose: () => void
  onFocusFirstEditable: () => void
  onDuplicate: () => void
  onViewHistory: () => void
  onSelectDraft: () => void
  onSelectSx: () => void
  onSelectCancel: () => void
  onDelete: () => void
  onToggleUnit: () => void
  onPushToDemand: () => void
}

export function SalesOrderRowMenu({
  line,
  canWrite,
  canDemandWrite,
  isOpen,
  anchorRect,
  onClose,
  onFocusFirstEditable,
  onDuplicate,
  onViewHistory,
  onSelectDraft,
  onSelectSx,
  onSelectCancel,
  onDelete,
  onToggleUnit,
  onPushToDemand,
}: SalesOrderRowMenuProps): React.ReactElement | null {
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    const handleScroll = () => {
      onClose()
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousedown', handleClickOutside, true)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousedown', handleClickOutside, true)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen, onClose])

  if (!isOpen || !anchorRect) return null

  // Calculate coordinates
  const winWidth = typeof window !== 'undefined' ? window.innerWidth : 1200
  const winHeight = typeof window !== 'undefined' ? window.innerHeight : 800

  const menuWidth = 240
  let left = anchorRect.left - menuWidth + anchorRect.width
  if (left < 10) left = 10
  if (left + menuWidth > winWidth - 10) {
    left = winWidth - menuWidth - 10
  }

  let top = anchorRect.bottom + 4
  if (top + 320 > winHeight && anchorRect.top - 320 > 10) {
    top = anchorRect.top - 320
  }

  const isDraft = line.line_status === 'draft'
  const isSx = line.line_status === 'sx'
  const isCancelled = line.line_status === 'cancelled'
  const isPushedDemand = line.demand_status === 'PUSHED' || Boolean(line.demand_batch_ids && line.demand_batch_ids.length > 0)
  const canDelete = isDraft && !isPushedDemand && canWrite

  const currentUnit = line.dim_unit || 'inch'
  const unitToggleLabel = currentUnit === 'inch' ? 'Đổi sang MM' : 'Đổi sang INCH'

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        zIndex: 999999,
      }}
      className="w-60 rounded-md border border-slate-300 bg-white py-1 text-sm shadow-xl animate-in fade-in zoom-in-95 duration-100"
      role="menu"
    >
      {/* Sửa */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onClose()
          onFocusFirstEditable()
        }}
        disabled={!canWrite || isCancelled}
      >
        <span>✏️</span> <span>Sửa dòng</span>
      </button>

      {/* Nhân đôi */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onClose()
          onDuplicate()
        }}
        disabled={!canWrite}
      >
        <span>📋</span> <span>Nhân đôi (Ctrl+D)</span>
      </button>

      {/* Lịch sử */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100"
        onClick={() => {
          onClose()
          onViewHistory()
        }}
      >
        <span>⏱️</span> <span>Xem lịch sử dòng</span>
      </button>

      <div className="my-1 border-t border-slate-200" />

      {/* Nháp */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onClose()
          onSelectDraft()
        }}
        disabled={!canWrite || isDraft}
      >
        <span>🔄</span>
        <span>{isCancelled ? 'Khôi phục về Nháp' : 'Chuyển về Nháp'}</span>
      </button>

      {/* SX */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onClose()
          onSelectSx()
        }}
        disabled={!canWrite || isSx || isCancelled}
      >
        <span>🚀</span> <span>Chuyển sang SX</span>
      </button>

      {/* Hủy */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onClose()
          onSelectCancel()
        }}
        disabled={!canWrite || isCancelled}
      >
        <span>✕</span> <span>Hủy dòng</span>
      </button>

      <div className="my-1 border-t border-slate-200" />

      {/* Xóa */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-slate-400"
        onClick={() => {
          onClose()
          onDelete()
        }}
        disabled={!canDelete}
        title={!isDraft ? 'Hãy Hủy thay vì Xóa' : isPushedDemand ? 'Dòng đã vào nhu cầu, không thể xóa' : undefined}
      >
        <span>🗑️</span>
        <span>
          Xóa dòng {!isDraft && <span className="text-xs text-slate-400">(Hãy Hủy thay vì Xóa)</span>}
        </span>
      </button>

      <div className="my-1 border-t border-slate-200" />

      {/* Đổi đơn vị kích thước */}
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100"
        onClick={() => {
          onClose()
          onToggleUnit()
        }}
      >
        <span>📐</span> <span>{unitToggleLabel}</span>
      </button>

      {/* Đưa vào nhu cầu (chỉ khi đang SX và có quyền demand.write) */}
      {isSx && canDemandWrite && (
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left font-semibold text-sky-700 hover:bg-sky-50"
          onClick={() => {
            onClose()
            onPushToDemand()
          }}
        >
          <span>📦</span> <span>Đưa vào nhu cầu</span>
        </button>
      )}
    </div>
  )
}
